import * as React from "react";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import { alpha } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import {
  ChartBarLine,
  ExitDoorLine,
  Home3Line,
  InboxLine,
  ListCheckLine,
  Message3Line,
  More2Line,
  PluginLine,
  Settings3Line,
  Transfer4Line,
} from "@mingcute/react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../../../../app/providers/AuthProvider";
import { ActionMenu } from "../../../../shared/components/ui";
import { getHasMultipleAccounts } from "../../../../shared/lib/auth/accountContext";

const SHOW_SHELL_NOTIFICATIONS = false;

export const APP_SHELL = {
  sidebarExpandedWidth: 264,
  sidebarCompactWidth: 72,
  topbarHeight: 64,
  mobileDrawerWidth: 320,
  mobileHeaderHeight: 64,
  desktopBreakpoint: "md",
};

const navSections = [
  {
    title: "Principal",
    items: [
      { label: "Dashboard", href: "/painel", icon: Home3Line },
      { label: "Canais", href: "/canais", icon: InboxLine },
      { label: "Listagens", href: "/listagens", icon: ListCheckLine },
      { label: "Modelos", href: "/modelos", icon: Message3Line },
      { label: "Campanhas", href: "/campanhas", icon: ChartBarLine },
    ],
  },
  {
    title: "Gestão",
    items: [
      { label: "Integrações", href: "/integracoes", icon: PluginLine },
      {
        label: "Configurações",
        href: "/configuracoes/informacoes-da-conta",
        activePrefix: "/configuracoes",
        icon: Settings3Line,
      },
    ],
  },
];

function isNavItemActive(pathname, item) {
  const activePath = item.activePrefix || item.href;
  return pathname === activePath || pathname.startsWith(`${activePath}/`);
}

export function getPrivatePageTitle(pathname) {
  const decoded = decodeURIComponent(pathname);
  const allItems = navSections.flatMap((section) => section.items);
  const match = allItems
    .filter((item) => isNavItemActive(decoded, item))
    .sort((first, second) => {
      const firstPath = first.activePrefix || first.href;
      const secondPath = second.activePrefix || second.href;
      return secondPath.length - firstPath.length;
    })[0];

  return match?.label || "Título da página";
}

export function getPrivateShellColors(theme, sidebarDark) {
  const custom = theme.palette.custom;

  if (sidebarDark) {
    return {
      activeBg: theme.palette.action.selected,
      activeText: custom.purple[200],
      background: custom.gray[900],
      border: custom.gray[700],
      brand: custom.purple[500],
      icon: custom.gray[500],
      muted: custom.gray[500],
      navText: custom.gray[300],
      scrim: alpha(theme.palette.common.black, 0.42),
      surface: custom.gray[800],
      text: custom.gray[50],
    };
  }

  return {
    activeBg: custom.purple[50],
    activeText: theme.palette.primary.main,
    background: theme.palette.background.default,
    border: theme.palette.border.default,
    brand: theme.palette.primary.main,
    icon: custom.gray[300],
    muted: theme.palette.text.secondary,
    navText: theme.palette.text.secondary,
    scrim: alpha(theme.palette.custom.gray[900], 0.36),
    surface: theme.palette.background.paper,
    text: theme.palette.text.primary,
  };
}

function Brand({ compact = false, colors, labelVisible = !compact, rail = compact }) {
  return (
    <Box
      sx={{
        alignItems: compact ? "center" : "flex-start",
        display: "flex",
        flexDirection: "column",
        minHeight: 72,
        px: compact ? 0 : 3,
        pt: 3,
        width: "100%",
        "@media (max-height: 760px)": {
          minHeight: 60,
          pt: 2.25,
        },
      }}
    >
      <Typography
        component="div"
        sx={{
          color: colors.brand,
          fontSize: 18,
          fontWeight: 800,
          lineHeight: 1.35,
          opacity: rail || labelVisible ? 1 : 0,
          overflow: "hidden",
          textAlign: compact ? "center" : "left",
          transition: "opacity 120ms ease, font-size 180ms ease",
          whiteSpace: "nowrap",
          "@media (max-height: 760px)": {
            fontSize: rail ? 17 : 16,
          },
        }}
      >
        {rail ? "123" : "123zap"}
      </Typography>
      <Box
        aria-hidden="true"
        sx={{
          bgcolor: colors.icon,
          borderRadius: 1,
          height: 4,
          mt: 0.5,
          opacity: compact || !labelVisible ? 0 : 1,
          transition: "opacity 120ms ease, width 180ms ease, height 180ms ease",
          width: compact ? 0 : 104,
          "@media (max-height: 760px)": {
            height: 3,
            width: compact ? 0 : 88,
          },
        }}
      />
    </Box>
  );
}

function NavItem({ compact, colors, item, active, labelVisible, onClick }) {
  const Icon = item.icon;
  const showLabel = !compact && labelVisible;
  const content = (
    <ButtonBase
      type="button"
      onClick={onClick}
      aria-label={item.label}
      sx={{
        alignItems: "center",
        bgcolor: active ? colors.activeBg : "transparent",
        borderRadius: 2,
        color: active ? colors.activeText : colors.navText,
        display: "flex",
        gap: showLabel ? 1.5 : 0,
        height: 40,
        justifyContent: compact ? "center" : "flex-start",
        px: compact ? 0 : 2,
        transition: "width 220ms ease, gap 160ms ease, padding 220ms ease",
        width: compact ? 48 : 232,
        "@media (max-height: 760px)": {
          height: 36,
          width: compact ? 44 : 232,
        },
        "&:hover": {
          bgcolor: active ? colors.activeBg : "action.hover",
        },
        "&:focus-visible": {
          outline: (theme) => `2px solid ${theme.palette.border.focus}`,
          outlineOffset: 2,
        },
      }}
    >
      <Icon aria-hidden size={compact ? 19 : 18} color="currentColor" />
      <Typography
        component="span"
        sx={{
          color: "currentColor",
          fontSize: 13,
          fontWeight: active ? 700 : 500,
          lineHeight: "18px",
          maxWidth: showLabel ? 160 : 0,
          minWidth: 0,
          opacity: showLabel ? 1 : 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          transition: "opacity 120ms ease, max-width 180ms ease",
          whiteSpace: "nowrap",
          "@media (max-height: 760px)": {
            fontSize: 12,
            lineHeight: "16px",
          },
        }}
      >
        {item.label}
      </Typography>
    </ButtonBase>
  );

  if (!compact) return content;

  return (
    <Tooltip title={item.label} placement="right">
      {content}
    </Tooltip>
  );
}

function Navigation({
  compact = false,
  colors,
  labelVisible = !compact,
  mobile = false,
  onItemClick,
}) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const decoded = decodeURIComponent(pathname);

  function goTo(item) {
    navigate(item.href);
    onItemClick?.();
  }

  return (
    <Stack
      component="nav"
      spacing={mobile ? 1.25 : 3}
      sx={{
        alignItems: compact ? "center" : "stretch",
        flex: "1 1 auto",
        minHeight: 0,
        overflowY: "auto",
        overscrollBehavior: "contain",
        px: compact ? 1.5 : 2,
        py: mobile ? 1.5 : 2,
        scrollbarColor: `${colors.border} transparent`,
        scrollbarWidth: "thin",
        transition: "gap 180ms ease, padding 220ms ease",
        "&::-webkit-scrollbar": {
          width: 8,
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: colors.border,
          borderRadius: 999,
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "transparent",
        },
        "@media (max-height: 760px)": {
          py: mobile ? 1 : 1.25,
        },
      }}
    >
      {navSections.map((section) => (
        <Stack
          key={section.title}
          spacing={mobile ? 0.75 : 1}
          sx={{
            alignItems: compact ? "center" : "stretch",
            transition: "align-items 180ms ease",
          }}
        >
          <Typography
            sx={{
              color: colors.navText,
              fontSize: 11,
              fontWeight: 600,
              height: 17,
              lineHeight: "17px",
              opacity: compact || !labelVisible ? 0 : 1,
              overflow: "hidden",
              px: 1,
              transition: "opacity 120ms ease",
            }}
          >
            {section.title}
          </Typography>

          <Stack
            spacing={mobile ? 0.75 : 1}
            sx={{
              alignItems: compact ? "center" : "stretch",
              transition: "align-items 180ms ease",
            }}
          >
            {section.items.map((item) => {
              const active = isNavItemActive(decoded, item);

              return (
                <NavItem
                  key={item.href}
                  compact={compact}
                  colors={colors}
                  item={item}
                  active={active}
                  labelVisible={labelVisible}
                  onClick={() => goTo(item)}
                />
              );
            })}
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
}

function UserFooter({
  compact = false,
  colors,
  labelVisible = !compact,
  onLogout,
  onSwitchAccount,
}) {
  const { user } = useAuth();
  const initial = (user?.name || user?.email || "Z").trim().charAt(0).toUpperCase();
  const title = user?.name || "Conta 123zap";
  const meta = user?.email || "Sessão ativa";
  const showLabel = !compact && labelVisible;
  const canSwitchAccount =
    Number(user?.accountsCount || 0) > 1 ||
    getHasMultipleAccounts(user?.email);
  const avatar = (
    <Box
      sx={{
        alignItems: "center",
        bgcolor: colors.activeBg,
        border: `1px solid ${colors.border}`,
        borderRadius: "50%",
        color: colors.activeText,
        display: "flex",
        flexShrink: 0,
        fontSize: 13,
        fontWeight: 800,
        height: 28,
        justifyContent: "center",
        width: 28,
      }}
    >
      {initial}
    </Box>
  );

  return (
    <Box
      sx={{
        p: compact ? 1.5 : 3,
        pt: 2,
        flex: "0 0 auto",
        transition: "padding 220ms ease",
        "@media (max-height: 760px)": {
          p: compact ? 1.25 : 2,
          pt: 1.5,
        },
      }}
    >
      <Divider
        sx={{
          borderColor: colors.border,
          mb: showLabel ? 2.5 : 0,
          opacity: showLabel ? 1 : 0,
          transition: "opacity 120ms ease, margin 180ms ease",
        }}
      />
      <Stack
        direction="row"
        spacing={showLabel ? 1.5 : 0}
        sx={{
          alignItems: "center",
          bgcolor: showLabel ? colors.background : "transparent",
          border: showLabel ? `1px solid ${colors.border}` : 0,
          borderRadius: 2,
          justifyContent: compact ? "center" : "flex-start",
          minHeight: compact ? 48 : 56,
          px: showLabel ? 1 : 0,
          transition: "background-color 160ms ease, border-color 160ms ease, padding 220ms ease",
          width: "100%",
          "@media (max-height: 760px)": {
            minHeight: compact ? 44 : 50,
          },
        }}
      >
        {compact ? null : avatar}
        <Box
          sx={{
            flex: 1,
            maxWidth: showLabel ? 132 : 0,
            minWidth: 0,
            opacity: showLabel ? 1 : 0,
            overflow: "hidden",
            transition: "opacity 120ms ease, max-width 180ms ease",
          }}
        >
          <Typography
            sx={{
              color: colors.text,
              fontSize: 12,
              fontWeight: 700,
              lineHeight: "16px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              color: colors.muted,
              fontSize: 11,
              lineHeight: "16px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {meta}
          </Typography>
        </Box>
        <ActionMenu
          ariaLabel="Abrir opções da conta"
          minWidth={132}
          placement="top-end"
          actions={[
            {
              id: "switch-account",
              icon: <Transfer4Line aria-hidden size={18} color="currentColor" />,
              label: "Trocar conta",
              hidden: !canSwitchAccount,
              onClick: onSwitchAccount,
            },
            {
              id: "logout",
              icon: <ExitDoorLine aria-hidden size={18} color="currentColor" />,
              label: "Sair",
              onClick: onLogout,
            },
          ]}
          renderTrigger={({ buttonProps }) => (
            compact ? (
              <Tooltip title="Opções da conta" placement="right">
                <ButtonBase
                  {...buttonProps}
                  sx={{ borderRadius: "50%" }}
                >
                  {avatar}
                </ButtonBase>
              </Tooltip>
            ) : showLabel ? (
              <ButtonBase
                {...buttonProps}
                sx={{
                  borderRadius: 1.5,
                  color: colors.muted,
                  p: 0.75,
                  "&:hover": { color: colors.text, bgcolor: "action.hover" },
                }}
              >
                <More2Line aria-hidden size={18} color="currentColor" />
              </ButtonBase>
            ) : null
          )}
        />
      </Stack>
    </Box>
  );
}

function DrawerActions({ colors, onClose, onToggleDarkMode, sidebarDark }) {
  function openDocumentation() {
    window.open("https://123zap.gitbook.io/docs", "_blank", "noopener,noreferrer");
    onClose?.();
  }

  return (
    <Stack
      spacing={1.5}
      sx={{
        flex: "0 0 auto",
        px: { xs: 2, sm: 3 },
        pt: 1,
        "@media (max-height: 760px)": {
          pt: 0.5,
        },
      }}
    >
      <Box>
        <Typography sx={{ color: colors.navText, fontSize: 11, fontWeight: 700, mb: 1 }}>
          Ações
        </Typography>
        <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
          <ButtonBase
            type="button"
            onClick={openDocumentation}
            sx={{
              alignItems: "center",
              border: `1px solid ${colors.border}`,
              borderRadius: 2,
              color: colors.muted,
              display: "flex",
              gap: 1,
              height: 48,
              justifyContent: "flex-start",
              px: 2,
              minWidth: 0,
              width: "100%",
              "@media (max-height: 760px)": {
                height: 42,
              },
            }}
          >
            <Typography sx={{ fontSize: 12, fontWeight: 700 }}>Guias</Typography>
          </ButtonBase>
          {SHOW_SHELL_NOTIFICATIONS ? (
            <ButtonBase
              type="button"
              sx={{
                alignItems: "center",
                border: `1px solid ${colors.border}`,
                borderRadius: 2,
                color: colors.muted,
                display: "flex",
                gap: 1,
                height: 48,
                justifyContent: "flex-start",
                px: 2,
                minWidth: 0,
                width: "100%",
                "@media (max-height: 760px)": {
                  height: 42,
                },
              }}
            >
              <NotificationsNoneRoundedIcon sx={{ color: colors.activeText, fontSize: 18 }} />
              <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Notificações</Typography>
            </ButtonBase>
          ) : null}
        </Stack>
      </Box>

      <Box>
        <Divider sx={{ borderColor: colors.border, mb: 2 }} />
        <Typography sx={{ color: colors.navText, fontSize: 11, fontWeight: 700, mb: 1 }}>
          Preferências
        </Typography>
        <ButtonBase
          type="button"
          onClick={onToggleDarkMode}
          sx={{
            alignItems: "center",
            border: `1px solid ${colors.border}`,
            borderRadius: 2,
            color: colors.muted,
            display: "flex",
            height: 44,
            justifyContent: "flex-start",
            px: 2,
            width: "100%",
            "@media (max-height: 760px)": {
              height: 40,
            },
          }}
        >
          <DarkModeOutlinedIcon sx={{ fontSize: 16, mr: 1.5 }} />
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Tema</Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 600, ml: "auto" }}>
            {sidebarDark ? "Dark" : "Light"}
          </Typography>
        </ButtonBase>
      </Box>
    </Stack>
  );
}

export default function MenuContent({
  compact = false,
  labelVisible = !compact,
  mobile = false,
  onItemClick,
  onToggleDarkMode,
  rail = compact,
  sidebarDark = false,
}) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = getPrivateShellColors(theme, sidebarDark);

  return (
    <Box
      sx={{
        "--app-shell-active-bg": colors.activeBg,
        "--app-shell-active-text": colors.activeText,
        "--app-shell-bg": colors.background,
        "--app-shell-border": colors.border,
        "--app-shell-brand": colors.brand,
        "--app-shell-muted": colors.muted,
        "--app-shell-surface": colors.surface,
        "--app-shell-text": colors.text,
        bgcolor: colors.surface,
        borderRight: `1px solid ${colors.border}`,
        boxSizing: "border-box",
        color: colors.text,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        transition: "background-color 180ms ease, border-color 180ms ease",
        width: "100%",
      }}
    >
      <Brand
        compact={compact && !mobile}
        colors={colors}
        labelVisible={labelVisible || mobile}
        rail={rail && !mobile}
      />
      <Navigation
        compact={compact && !mobile}
        colors={colors}
        labelVisible={labelVisible || mobile}
        mobile={mobile}
        onItemClick={onItemClick}
      />
      {mobile ? (
        <DrawerActions
          colors={colors}
          onClose={onItemClick}
          onToggleDarkMode={onToggleDarkMode}
          sidebarDark={sidebarDark}
        />
      ) : null}
      <Box
        sx={{
          minHeight: 0,
          mt: mobile ? 2 : "auto",
        }}
      >
        <UserFooter
          compact={compact && !mobile}
          colors={colors}
          labelVisible={labelVisible || mobile}
          onLogout={async () => {
            await logout();
            onItemClick?.();
          }}
          onSwitchAccount={() => {
            navigate("/selecionar-conta?modo=trocar", {
              state: { mode: "switch-account" },
            });
            onItemClick?.();
          }}
        />
      </Box>
    </Box>
  );
}
